import { Injectable } from "@angular/core";
import { collection, collectionData, Firestore, orderBy, query, Timestamp, where } from "@angular/fire/firestore";
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, of, shareReplay, switchMap, takeUntil } from "rxjs";
import { FireSubService } from "src/app/core/firebase/fireSub.service";
import { ProfileStateDispatcher } from "src/app/core/state/profile/profile.dispatcher";
import {
  IBaseSopoWalletTransaction,
  SopoWalletTransactionType,
} from "@soposopo/sopo-models"

const ONE_DAY_IN_MILISECONDS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_IN_MILISECONDS = 7 * ONE_DAY_IN_MILISECONDS;
const ONE_MONTH_IN_MILISECONDS = 30 * ONE_DAY_IN_MILISECONDS;

const SAFE_RECALCULATE_IN_MILISECONDS = 5 * 60 * 1000; // 5 minutes

@Injectable({
  providedIn: 'root'
})
export class CoinflowWithdrawService {
  private _isWithdrawAllowed$: Observable<IIsWithdrawAllowed> | undefined;
  private _recalculate: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private _recalculateTimeout: NodeJS.Timeout;

  get isWithdrawAllowed(): Observable<IIsWithdrawAllowed> {
    if (!this._isWithdrawAllowed$) {
      this.initializeWithdrawRestriction();
    }
    return this._isWithdrawAllowed$;
  }

  constructor(
    private firestore: Firestore,
    private profileStateDispatcher: ProfileStateDispatcher,
    private fireSubService: FireSubService,
  ) { }

  public clearIsWithdrawAllowed(): void {
    this._isWithdrawAllowed$ = undefined;
    clearTimeout(this._recalculateTimeout);
  }

  private initializeWithdrawRestriction() {
    const userId$ = this.profileStateDispatcher.getProfileState().pipe(
      map(profileState => profileState.profileData.uid),
      filter(userId => !!userId),
      distinctUntilChanged(),
    );
    this._isWithdrawAllowed$ = combineLatest([userId$, this._recalculate]).pipe(
      switchMap(([userId, _]) => {
        const midnight = new Date(this.getMidnightISOString());
        this.setRecalculateTimeout();

        const thirtyDaysAgo = new Date(midnight.getTime() - ONE_MONTH_IN_MILISECONDS).toISOString();
        const firestoreTimestamp = Timestamp.fromDate(new Date(thirtyDaysAgo));
  
        const sopoWalletTransactionsRef = collection(this.firestore, 'sopoWalletTransactions');
        const sopoWalletTransactionsQuery = query(sopoWalletTransactionsRef,
          where('userId', '==', userId),
          where('transactionType', '==', SopoWalletTransactionType.WITHDRAW),
          where('createdAt', '>=', firestoreTimestamp),
          orderBy('createdAt', 'desc'),
        );
        const swTransactionsCollectionData = collectionData(sopoWalletTransactionsQuery);

        const wager$ = this.profileStateDispatcher.getExperienceObservable().pipe(
          map(experienceData => experienceData.totalWagesSB),
          distinctUntilChanged(),
      );

        return combineLatest([swTransactionsCollectionData, wager$]).pipe(
          map(([transactions, wager]: [IBaseSopoWalletTransaction[], number]) => this.splitIntoTimeBuckets(transactions, midnight, wager)),
          map((bucketedTransactions: ISplitIntoTimeBuckets) => this.checkRestrictions(bucketedTransactions)),
        );
      }),
      takeUntil(this.fireSubService.unsubscriber),
      shareReplay({bufferSize: 1, refCount: true}),
    );
  }

  // #region SopoWalletTransaction Helpers

  private splitIntoTimeBuckets(transactions: IBaseSopoWalletTransaction[], midnight: Date, wager: number): ISplitIntoTimeBuckets {
    const sevenDaysAgo = new Date(midnight.getTime() - SEVEN_DAYS_IN_MILISECONDS);

    const last1Day: IBaseSopoWalletTransaction[] = [];
    const last7Days: IBaseSopoWalletTransaction[] = [];

    for(const transaction of transactions) {
      if (transaction.createdAt.toDate() >= midnight) {
        last1Day.push(transaction);
        last7Days.push(transaction);
      } else if (transaction.createdAt.toDate() >= sevenDaysAgo) {
        last7Days.push(transaction);
      }
    }

    return {
      buckets: {
        last1Day,
        last7Days,
        last30Days: transactions,
      },
      wager
    };
  }

  private checkRestrictions(bucketedTransactions: ISplitIntoTimeBuckets): IIsWithdrawAllowed {
    let isAllowed = true;
    let reasons: string[] = [];

    const last1DayTotal = bucketedTransactions.buckets.last1Day.reduce((acc, curr) => acc + curr.amount, 0);
    const last7DaysTotal = bucketedTransactions.buckets.last7Days.reduce((acc, curr) => acc + curr.amount, 0);
    const last30DaysTotal = bucketedTransactions.buckets.last30Days.reduce((acc, curr) => acc + curr.amount, 0);

    if (bucketedTransactions.wager <= 0) {
      isAllowed = false;
      reasons.push('You must play at least one game with your Sweeps Coins before you can withdraw.');
    }

    if (last1DayTotal >= Limits.ONE_DAY) {
      isAllowed = false;
      reasons.push('You have reached the 1-day transaction limit of $5,000.');
    }
    if (last7DaysTotal >= Limits.SEVEN_DAYS) {
      isAllowed = false;
      reasons.push('You have reached the 7-day transaction limit of $10,000.');
    }
    if (last30DaysTotal >= Limits.ONE_MONTH){
      isAllowed = false;
      reasons.push('You have reached the 30-day transaction limit of $20,000.');
    }

    const amountAvailableToday = this.getMaxWithdrawableAmountToday(last1DayTotal, last7DaysTotal, last30DaysTotal) / 100;

    return {
      isAllowed,
      amountAvailableToday,
      reasons
    };
  }

  // #endregion SopoWalletTransaction Helpers

  private getMidnightISOString(): string {
    const now = new Date();
    const midnightUTC = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0, 0, 0, 0
    ));

    return midnightUTC.toISOString();
  }

  private setRecalculateTimeout(): void {
    clearTimeout(this._recalculateTimeout);
    
    const now = new Date();
    const nextMidnight = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0, 0, 0, 0
    )).toISOString();

    const delay = new Date(nextMidnight).getTime() - now.getTime();
    const safeDelay = delay > SAFE_RECALCULATE_IN_MILISECONDS ? delay : SAFE_RECALCULATE_IN_MILISECONDS;
    if (safeDelay > 0) {
      this._recalculateTimeout = setTimeout(() => this._recalculate.next(0), delay);
    }
  }

  private getMaxWithdrawableAmountToday(last1DayTotal: number, last7DaysTotal: number, last30DaysTotal: number): number {
    const remaining1Day = Limits.ONE_DAY - last1DayTotal;
    const remaining7Days = Limits.SEVEN_DAYS - last7DaysTotal;
    const remaining30Days = Limits.ONE_MONTH - last30DaysTotal;

    return Math.max(Math.min(remaining1Day, remaining7Days, remaining30Days), 0);
  }

}

enum Limits {
  ONE_DAY = 500000, // $5.000 in cents
  SEVEN_DAYS = 1000000, // $10.000 in cents
  ONE_MONTH = 2000000, // $20.000 in cents
}

export enum LimitsInDollar {
  ONE_DAY = 5000, // $5.000
  SEVEN_DAYS = 10000, // $10.000
  ONE_MONTH = 20000, // $20.000
}


interface ISplitIntoTimeBuckets {
  buckets: {
    last1Day: IBaseSopoWalletTransaction[];
    last7Days: IBaseSopoWalletTransaction[];
    last30Days: IBaseSopoWalletTransaction[];
  }
  wager: number;
}

export interface IIsWithdrawAllowed {
  isAllowed: boolean;
  amountAvailableToday: number; 
  reasons: string[];
}
